import View from '@play-co/timestep-core/lib/ui/View';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import uiConfig from 'src/lib/ui/config';
import { downscale } from 'src/conf/scale';
import StateObserver from 'src/StateObserver';
import { Feature } from 'src/replicant/analytics';
import { isShareTargetPickerAvailable } from 'src/lib/sharing';
import {
  shareJankenChallenge,
  type JankenChallengeType,
} from 'src/game/janken/janken.share';
import type { JankenMove } from 'src/replicant/janken/janken.ruleset';
import gsap from 'gsap';
import sounds from 'src/lib/sounds';

const btnWidth = downscale(443);
const btnHeight = downscale(151);

type JankenShareButtonsOpts = {
  superview: View;
  x: number;
  y: number;
  width: number;
};

/**
 * Self-contained share button row for the Janken challenge screen.
 *
 * Displays share buttons using a FlexBoxView row layout with locale-specific
 * icon images. When LINE Share Target Picker is available, shows both the
 * LINE and native share buttons. Otherwise shows only the native share button.
 *
 * Owns all share logic: calling {@link shareJankenChallenge} and tracking
 * the `JankenSend` analytics event. The popup only needs to call
 * {@link showForChallenge} to display and {@link reset} to hide.
 *
 * Starts hidden — call {@link showForChallenge} to fade in.
 */
export default class JankenShareButtons extends View {
  private challengeId: string | null = null;
  private myMove: JankenMove | null = null;
  private challengeType: JankenChallengeType = 'challenge';
  private btnLine: ButtonScaleView;

  constructor(opts: JankenShareButtonsOpts) {
    const locale = StateObserver.getLocale();

    super({
      superview: opts.superview,
      x: opts.x,
      y: opts.y,
      width: opts.width,
      height: btnHeight,
      centerOnOrigin: true,
      visible: false,
      opacity: 0,
    });

    const buttonRow = new FlexBoxView({
      superview: this as View,
      x: 0,
      y: 0,
      width: opts.width,
      height: btnHeight,
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    this.btnLine = new ButtonScaleView({
      superview: buttonRow,
      ...uiConfig.buttons.secondary,
      width: btnWidth,
      height: btnHeight,
      icon: {
        image: `assets/ui/janken/icons/btn_line_${locale}.png`,
        width: downscale(365),
        height: downscale(102),
        offset: { x: 0, y: -5 },
      },
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.POPUP_CTA,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.share('line');
      },
    });

    new ButtonScaleView({
      superview: buttonRow,
      ...uiConfig.buttons.blue,
      width: btnWidth,
      height: btnHeight,
      icon: {
        image: `assets/ui/janken/icons/btn_others_${locale}.png`,
        width: downscale(362),
        height: downscale(102),
        offset: { x: 0, y: -5 },
      },
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.POPUP_CTA,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.share('native');
      },
    });

    for (const child of buttonRow.getSubviews()) {
      getFlexNode(child).updateStyle({
        marginLeft: 5,
        marginRight: 5,
      });
    }
  }

  /** Store challenge data, make visible, and fade in. */
  public async showForChallenge(
    challengeId: string,
    myMove: JankenMove,
    challengeType: JankenChallengeType,
  ): Promise<void> {
    this.challengeId = challengeId;
    this.myMove = myMove;
    this.challengeType = challengeType;
    const showLine = isShareTargetPickerAvailable();
    this.btnLine.updateOpts({ visible: showLine });
    this.updateOpts({ visible: true, opacity: 0 });
    await gsap.to(this.style, { opacity: 1, duration: 0.3 });
  }

  /** Execute the share flow (JankenSend tracking is handled inside shareJankenChallenge). */
  private async share(shareMethod: 'line' | 'native'): Promise<void> {
    if (!this.challengeId || !this.myMove) return;
    await shareJankenChallenge(this.challengeId, {
      senderMove: this.myMove,
      challengeType: this.challengeType,
      forceNativeShare: shareMethod === 'native',
    });
  }

  /** Kill any running fade animations on this view. */
  public killAnimations(): void {
    gsap.killTweensOf(this.style);
  }

  /** Hide the view and clear stored challenge data. */
  public reset(): void {
    this.killAnimations();
    this.challengeId = null;
    this.myMove = null;
    this.challengeType = 'challenge';
    this.updateOpts({ visible: false, opacity: 0 });
  }
}
